// GET /api/firm/assignments - per-member workload counts. The route is
// admin-gated by design: a 403 is a normal answer ("not allowed"),
// distinct from an unreachable server, which throws. Widgets degrade to
// the people without the numbers - never to invented percents.

import { getAuthHeaders } from "./auth";
import type { FirmStaff } from "./firmStaff";

export interface StaffWorkloadRow {
  user: FirmStaff;
  activeCount: number;
  pendingCount: number;
  closedThisMonth: number;
  overdueCount: number;
  workloadHeat: "balanced" | "heavy" | "overloaded";
}

export interface FirmWorkload {
  allowed: boolean;
  workload: StaffWorkloadRow[];
}

export async function fetchFirmWorkload(): Promise<FirmWorkload> {
  const res = await fetch("/api/firm/assignments", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (res.status === 403) return { allowed: false, workload: [] };
  if (!res.ok) throw new Error(`Workload failed: ${res.status}`);
  const payload = (await res.json()) as { workload?: StaffWorkloadRow[] };
  return { allowed: true, workload: payload.workload ?? [] };
}

export interface AssignedCase {
  _id: string;
  caseNumber?: string;
  eId?: string;
  caseType?: string;
  caseStage?: string;
  status?: string;
  clientData?: { personal?: { firstName?: string; lastName?: string } };
  deadlines?: Array<{ completed?: boolean }>;
}

export interface MemberAssignments {
  allowed: boolean;
  cases: AssignedCase[];
}

// GET /api/firm/assignments/:userId - one member's assigned cases, under
// the same 403-is-an-answer contract as the firm-wide feed above.
export async function fetchMemberAssignments(userId: string): Promise<MemberAssignments> {
  const res = await fetch(`/api/firm/assignments/${encodeURIComponent(userId)}`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (res.status === 403) return { allowed: false, cases: [] };
  if (!res.ok) throw new Error(`Assignments failed: ${res.status}`);
  const payload = (await res.json()) as { cases?: AssignedCase[] };
  return { allowed: true, cases: payload.cases ?? [] };
}
